import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Download, Trash2, ShieldCheck, Loader2 } from "lucide-react";

export default function Settings() {
  const nav = useNavigate();
  const { user, loading, logout } = useAuth();
  const [consent, setConsent] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState("");

  useEffect(() => {
    if (!loading && !user) nav("/login");
  }, [user, loading, nav]);

  useEffect(() => {
    if (user) api.get("/me/consent").then((r) => setConsent(r.data)).catch(() => {});
  }, [user]);

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/me/export");
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `partbypart-my-data-${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Data exported");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const deleteAccount = async () => {
    if (confirmDel !== "DELETE") return toast.error('Type DELETE to confirm');
    setBusy(true);
    try {
      await api.post("/me/delete");
      toast.success("Your account has been erased.");
      await logout();
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Deletion failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="pbp-section py-12 md:py-16 max-w-3xl">
      <div className="text-xs uppercase tracking-[0.18em] text-[#86868b] font-semibold">Account</div>
      <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">Settings &amp; privacy</h1>

      <section className="mt-8 pbp-card p-6">
        <h2 className="text-lg font-semibold tracking-tight">Consent</h2>
        <p className="text-sm text-[#86868b] mt-1">
          Current consent: <b>{consent?.consent_version || "—"}</b> &middot; last updated {consent?.consent_given_at ? new Date(consent.consent_given_at).toLocaleString() : "—"}.
          You can <a className="pbp-link" href="/consent">review or update your consent</a> or use "Delete my account" below to withdraw consent entirely.
        </p>
        <div className="mt-3 text-xs text-[#86868b]">Purposes agreed: {(consent?.purposes || []).join(", ") || "—"}</div>
      </section>

      <section className="mt-6 pbp-card p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#0071e3]/10 grid place-items-center text-[#0071e3]"><Download className="w-5 h-5" strokeWidth={1.6} /></div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold tracking-tight">Download your data</h2>
            <p className="text-sm text-[#86868b] mt-1">
              Under the DPDP Act 2023 &amp; GDPR, you have the right to access all personal data we
              hold. Includes your profile, listings, chats and notifications as a single JSON file.
            </p>
            <button data-testid="settings-export" disabled={busy} onClick={download} className="pbp-btn-secondary mt-4 text-[13px]">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Download my data (JSON)
            </button>
          </div>
        </div>
      </section>

      <section className="mt-6 pbp-card p-6 border-2 border-[#ff3b30]/30">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#ff3b30]/10 grid place-items-center text-[#ff3b30]"><Trash2 className="w-5 h-5" strokeWidth={1.6} /></div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold tracking-tight">Delete my account</h2>
            <p className="text-sm text-[#86868b] mt-1">
              Permanently erase your account, remove your active listings, anonymise your chat
              messages, and revoke all sessions. This action is irreversible. Retained: minimal
              tombstone record for legal/audit obligations under the IT Act, 2000.
            </p>
            <div className="mt-4 flex flex-wrap gap-2 items-center">
              <input
                data-testid="settings-delete-confirm"
                value={confirmDel}
                onChange={(e) => setConfirmDel(e.target.value)}
                placeholder="Type DELETE to confirm"
                className="pbp-input !py-1.5 text-[13px] max-w-xs"
              />
              <button
                data-testid="settings-delete"
                disabled={busy || confirmDel !== "DELETE"}
                onClick={deleteAccount}
                className="pbp-btn-primary bg-[#ff3b30] hover:bg-[#e5342b] focus-visible:ring-[#ff3b30]/30 text-[13px]"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                Erase my account
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="mt-6 pbp-card p-6">
        <div className="flex items-start gap-4">
          <div className="w-10 h-10 rounded-xl bg-[#34c759]/10 grid place-items-center text-[#34c759]"><ShieldCheck className="w-5 h-5" strokeWidth={1.6} /></div>
          <div>
            <h2 className="text-lg font-semibold tracking-tight">Grievance officer</h2>
            <p className="text-sm text-[#86868b] mt-1">
              For any privacy or data grievance, contact our Grievance Officer at
              <a className="pbp-link ml-1" href="mailto:grievance@partbypart.com">grievance@partbypart.com</a>.
              Acknowledgement within 24 hours &middot; resolution within 15 days &middot; Mumbai, Maharashtra jurisdiction.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
}
